import { createFileRoute, Outlet } from "@tanstack/react-router";
import LayoutSidebarScrollAreas from "@/components/layout/LayoutSidebarScrollAreas";
import LeaSidebar from "@/components/layout/LeaSidebar";
import Loader from "@/components/shared/Loader";
import NotFound from "@/routes/-NotFound";
import ErrorPage from "@/routes/-ErrorPage";
import { roleRecordTypes } from "@/models";
import { hasRoleFn, preschoolDataQueryOptions } from "@/queries";
import { FileSubmissionsTracker } from "@/components/layout/FileSubmissionsTracker";
import { AddChildProvider } from "@/components/layout/AddChildContext";
import { dedupeByLatestEffective } from "@/utils/data-structures";

export const Route = createFileRoute("/$leaid")({
  component: LeaLayout,
  pendingComponent: () => {
    return (
      <LayoutSidebarScrollAreas>
        <Loader containerClass="py-4 spinner-navy" />
      </LayoutSidebarScrollAreas>
    );
  },
  notFoundComponent: NotFound,
  errorComponent: ErrorPage,
  beforeLoad: async ({ context, params }) => {
    const loginLeas = context.loginDetail.userOrgs;
    const currentUserOrg = loginLeas.find((x) => x.esiIdPath === params.leaid);
    if (currentUserOrg === undefined) {
      throw new Error("LEA could not be found");
    }
    const stateLevelOrg = loginLeas.find((x) => x.esiIdPath === "0-");
    const isStateLevelAdmin = !!hasRoleFn(
      context.coreData.securityRoles,
      stateLevelOrg
    )("ADMIN");
    const currentPreschools = await context.queryClient.ensureQueryData(
      preschoolDataQueryOptions(context, currentUserOrg.esiIdPath)
    );
    return {
      currentUserOrg: currentUserOrg,
      currentOrgPreschools: dedupeByLatestEffective(
        currentPreschools,
        "PreschoolID"
      ),
      currentOrgPreschoolsByProgramYear: (programYear: string) => {
        const py = context.coreData.programYears.find(
          (x) => x.Code == programYear
        );
        if (!py) return [];
        return currentPreschools.filter(
          (x) =>
            x.EffectiveStartDate <= py.EffectiveEndDate &&
            py.EffectiveStartDate <= (x.EffectiveEndDate ?? "9999-99-99")
        );
      },
      hasRole: hasRoleFn(context.coreData.securityRoles, currentUserOrg),
      isStateLevelAdmin: isStateLevelAdmin,
      editFilePermissions: () => {
        const coreData = context.coreData;
        const org = currentUserOrg;
        const currentRoles = org.roleIds.map(
          (x) =>
            coreData.securityRoles.find((r) => r.CAPSDACRoleId === x)
              ?.CAPSDACRoleName
        );
        return roleRecordTypes.filter((rt) =>
          currentRoles.find((r) => r === `${rt}Edit`)
        );
      },
    };
  },
});

function LeaLayout() {
  return (
    <AddChildProvider>
      <FileSubmissionsTracker>
        <LayoutSidebarScrollAreas sidebar={<LeaSidebar />}>
          <Outlet />
        </LayoutSidebarScrollAreas>
      </FileSubmissionsTracker>
    </AddChildProvider>
  );
}
